import { FormControl, FormHelperText } from "@mui/material";
import { useContext, useEffect, useMemo, useState, type FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { DropdownSourceContext } from "@ryde/redux";
import {
  errors,
  isInnerItemEditingAtom,
  Log,
  type ICustomField,
} from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { emptyUserRole } from "./table-user-roles.models";
import { TableUserRolesStyled } from "./table-user-roles.styled";
import { checkUserRoleListFieldData } from "./table-user-roles.utils";
import { UserRoleRow } from "./user-role-row";
import { UserRoleAdd } from "./user-role.add";

export const TableUserRolesTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const [managerRolesIds, setManagerRolesIds] = useState<Set<number>>(
    new Set(),
  );
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const watchedValue = useWatch({ control, name });
  const userRolesList = useMemo(() => {
    const userRolesListValue = checkUserRoleListFieldData(
      watchedValue,
      name,
      setValue,
    );
    Log.debug("UserRoles list initial", userRolesListValue);
    return userRolesListValue;
  }, [name, setValue, watchedValue]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove userRole handler fired for index ", index);
    userRolesList?.splice(index, 1);
    setValue(name, userRolesList, { shouldDirty: true, shouldValidate: true });
  });

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  useEffect(() => {
    const loadRolesOnInit = async (): Promise<void> => {
      const roles = await getDropdownItems("role");
      if (!roles?.length) {
        const debugMessage = "Failed to load dropdown items for role entities.";
        Log.warnAndToast(
          t(errors.common.technicalProblemOccurred),
          debugMessage,
        );
        return;
      }

      const ids = new Set(
        roles
          .filter(({ isNotManagerRole }) => !isNotManagerRole)
          .map(({ value }) => Number(value)),
      );

      setManagerRolesIds(ids);
    };

    loadRolesOnInit();
  }, [getDropdownItems, t]);

  return (
    <TableUserRolesStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="table-userRoles-template"
            >
              <GroupTitleTemplate
                formLabel={formLabel}
                name={formLabel + "_label"}
                formAttributes={formAttributes}
              />
              <div
                className="userRoles__container"
                data-inner-editing={editingCount > 0 ? "true" : undefined}
              >
                {userRolesList?.map((userRoleField, index) => {
                  const listField = userRoleField?.roleId
                    ? userRoleField
                    : emptyUserRole;

                  return (
                    managerRolesIds.has(userRoleField?.roleId) && (
                      <UserRoleRow
                        key={`$${index}_{listField.roleId}_${listField.siteIds}_${listField.supplierIds}`}
                        fieldName={name}
                        index={index}
                        removeHandler={removeHandler}
                        parentControl={control}
                        parentSetValue={setValue}
                        onEditStart={handleEditStart}
                        onEditEnd={handleEditEnd}
                      />
                    )
                  );
                })}
                <UserRoleAdd
                  fieldName={name}
                  index={userRolesList?.length || 0}
                  setValueOuter={setValue}
                  formAttributes={formAttributes}
                  onEditStart={handleEditStart}
                  onEditEnd={handleEditEnd}
                />
              </div>

              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </TableUserRolesStyled>
  );
};
